import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Box,
  Tabs,
  Tab,
} from '@mui/material';
import { DailyCall } from '@daily-co/daily-js';
import { useAppMessage, useDaily } from '@daily-co/daily-react';

import { useSessionContext } from '../../contexts/SessionContext';

const useStyles = () => {
  return {
    container: {
      paddingTop: '0rem',
      width: '100%',
      minHeight: '30rem',
      height: '75vh',
      '& .inputSelect': {
        width: 'calc(100% - 2rem)',
      },
      overflow: 'hidden',
    },
    button: {
      float: 'right',
    },
    paper: {
      maxWidth: '60rem',
      width: '60rem',
      minHeight: '30rem',
      overflow: 'hidden',
    },
    formControl: {
      display: 'block',
      margin: '1.5em 0',
      '&:first-child': {
        margin: '0 0 1.5em 0',
      },
    },
    label: {
      width: '133%',
    },
    drawer: {
      display: 'flex',
    },
    thumbnailContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      padding: '.3125rem',
      overflowY: 'auto',
    },
  };
};

const RoomMonitorDialog = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const styles = useStyles();
  const callObject = useDaily();
  const [localNetworkStats, setLocalNetworkStats] = useState<any>({});
  const [remoteNetworkStats, setRemoteNetworkStats] = useState<any>({});
  const [roomStats, setRoomStats] = useState<any>({});

  const [tabIndex, setTabIndex] = useState(0);

  //const { remoteVideoRef } = useLocalParticipant();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  useAppMessage({
    onAppMessage: ({ data, ...rest }: any) => {
      if (data?.type !== 'network-stats') return;

      setRemoteNetworkStats(data.stats);
    },
  });

  useEffect(() => {
    const getNetworkStats = async (callObject: DailyCall | null) => {
      if (!callObject) return;

      const networkStats = await callObject.getNetworkStats();
      const sendSettings = callObject.getSendSettings();
      const meetingState = callObject.meetingSessionState();
      const cpu =
        meetingState.topology === 'sfu'
          ? await callObject.getCpuLoadStats()
          : {};

      const participants = callObject.participants();
      const videoTracks: any[] = [];
      Object.entries(participants).forEach(([id, p]) => {
        if (p.tracks.video) {
          const videoTrack = p.tracks.video as any;
          videoTracks.push({
            id,
            width: videoTrack.width,
            height: videoTrack.height,
            state: videoTrack.state,
          });
        }
      });
      const localParticipant = callObject.participants().local;
      const videoTrack = localParticipant?.tracks?.video?.track;
      const videoSettings = videoTrack?.getSettings();

      //console.log('::::localNetworkStats', networkStats);//only show use turned on debugging

      setLocalNetworkStats({
        networkStats,
        sendSettings,

        videoTracks,
        videoSettings,
        cpu,
      });

      setRoomStats({
        participants,
        meetingState,
      });
    };

    const interval = setInterval(() => {
      getNetworkStats(callObject);
    }, 2500);

    return () => {
      clearInterval(interval);
    };
  }, [callObject]);

  return (
    <Dialog
      id="room-monitor-dialog"
      open={isOpen}
      onClose={onClose}
      PaperProps={{ sx: styles.paper }}>
      <DialogTitle>Connection Settings</DialogTitle>
      <Divider />
      <DialogContent sx={styles.container}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Tabs value={tabIndex} onChange={handleTabChange}>
            <Tab label="Room" />
            <Tab label="Local" />
            <Tab label="Remote" />
          </Tabs>
          <Box
            id="room-monitor-content"
            sx={{
              overflowY: 'auto',
              flexGrow: 1,
            }}>
            <Box hidden={tabIndex !== 0} sx={{ mt: 2 }}>
              <pre>{JSON.stringify(roomStats, null, 2)}</pre>
            </Box>
            <Box hidden={tabIndex !== 1} sx={{ mt: 2 }}>
              <pre>{JSON.stringify(localNetworkStats, null, 2)}</pre>
            </Box>
            <Box hidden={tabIndex !== 2} sx={{ mt: 2 }}>
              <pre>{JSON.stringify(remoteNetworkStats, null, 2)}</pre>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          sx={styles.button}
          color="primary"
          variant="contained"
          onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoomMonitorDialog;
